import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import _get from 'lodash/get';
import { saveAs } from 'file-saver';
import PropTypes from 'prop-types';

import { blockType } from '../../../../types';
import BananatagButton from '../../../../componentLibrary/BananatagButton';
import Modal from '../../../../componentLibrary/Modal';
import * as htmlOutputActions from '../../../../actions/htmlOutput';
import DownloadButton from './DownloadButton';
import { request } from '../../../../common/request';
import * as Styled from './styles';

const HtmlModal = ({ dispatch, draftID, blocks, toggleHtmlModal, showPersonalizationWarning }) => {
    const [htmlData, setHtmlData] = useState(null);
    const [isDownloadButtonEnabled, setIsDownloadButtonEnabled] = useState(false);
    const [hasError, setHasError] = useState(false);

    const getHtml = useCallback(async () => {
        try {
            setHasError(false);
            setIsDownloadButtonEnabled(false);

            const { html } = await request.convertHtml({
                draftID,
                blocks,
                useEmbeddedCustomFonts: false,
            });
            if (html) {
                dispatch(htmlOutputActions.updatedHtmlBody({ html }));

                setIsDownloadButtonEnabled(true);
                setHtmlData(html);
            } else {
                setHasError(true);
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
            setHasError(true);
        }
    }, [blocks]);

    const downloadHtmlFile = useCallback(() => {
        const blob = new Blob([htmlData], { type: 'text/plain;charset=utf-8' });
        saveAs(blob, `Bananatag-${Date.now()}.html`);
    }, [htmlData]);

    useEffect(() => {
        getHtml();
    }, [getHtml]);

    return (
        <Modal
            width={280}
            height={showPersonalizationWarning && !hasError ? 360 : 240}
            padding={20}
            borderRadius={8}
            labelledBy="download-html-modal-title"
            onEsc={toggleHtmlModal}
        >
            <Styled.Header id="download-html-modal-title">Download HTML</Styled.Header>
            <Styled.Alert hasError={hasError}>
                {hasError ? (
                    <>
                        <Styled.ErrorMessageIcon icon="times-octagon" />
                        <Styled.ErrorMessage>Oops, something went wrong, please try again.</Styled.ErrorMessage>
                    </>
                ) : (
                    <>
                        <Styled.MessageIcon icon="exclamation-circle" />
                        <Styled.MessageWrapper>
                            <Styled.MessageHeader>Fonts</Styled.MessageHeader>
                            <Styled.Message>
                                Fallback fonts will display if chosen fonts are not installed on the viewer&apos;s
                                device.
                            </Styled.Message>
                            {showPersonalizationWarning && (
                                <Styled.PersonalizationWrapper>
                                    <Styled.MessageHeader>Personalization Tags</Styled.MessageHeader>
                                    <Styled.Message>
                                        Draft containing tags will remain as tags and not shown its actual value in the
                                        HTML.
                                    </Styled.Message>
                                </Styled.PersonalizationWrapper>
                            )}
                        </Styled.MessageWrapper>
                    </>
                )}
            </Styled.Alert>
            <Styled.ButtonWrapper>
                <BananatagButton
                    type="clear"
                    size="small"
                    onClick={toggleHtmlModal}
                    text="cancel"
                    styling={{ marginRight: 10 }}
                />
                {hasError ? (
                    <BananatagButton type="solid" size="small" onClick={getHtml} icon="refresh" text="retry" />
                ) : (
                    <DownloadButton
                        enabled={isDownloadButtonEnabled}
                        buttonText="download"
                        onClick={downloadHtmlFile}
                    />
                )}
            </Styled.ButtonWrapper>
        </Modal>
    );
};

const mapStateToProps = (state) => ({
    draftID: _get(state, 'session.collaboration.id', ''),
    blocks: _get(state, 'canvasBlocks.present', []),
});

HtmlModal.defaultProps = {
    showPersonalizationWarning: false,
};

HtmlModal.propTypes = {
    dispatch: PropTypes.func.isRequired,
    draftID: PropTypes.string.isRequired,
    blocks: PropTypes.arrayOf(blockType).isRequired,
    toggleHtmlModal: PropTypes.func.isRequired,
    showPersonalizationWarning: PropTypes.bool,
};

export default connect(mapStateToProps)(HtmlModal);
